import { pagesDictionary } from '@/utils/dictionaries/pagesDictionary/pagesDictionary'

/**
 * Resolver for dynamic routes that match patterns like /members/[member] with actual URLs like /members/12345.
 */
class DynamicRouteResolver {
  /**
   * Checks if this resolver can handle the given pathname.
   * @param {string} pathname - The current pathname.
   * @returns {boolean} True if this resolver can handle the pathname.
   */
  canResolve(pathname) {
    // Check if pathname matches any dynamic route pattern in dictionary
    const patterns = Object.keys(pagesDictionary).filter((key) => key.includes('['))

    return patterns.some((pattern) => {
      const regex = new RegExp('^' + pattern.replaceAll(/\[[^\]]{0,100}\]/g, '[^/]+') + '$')
      return regex.test(pathname)
    })
  }

  /**
   * Resolves the page title for dynamic routes.
   * @param {string} pathname - The current pathname.
   * @returns {string} The resolved page title.
   */
  resolve(pathname) {
    const patterns = Object.keys(pagesDictionary).filter((key) => key.includes('['))

    for (const pattern of patterns) {
      const regex = new RegExp('^' + pattern.replaceAll(/\[[^\]]{0,100}\]/g, '[^/]+') + '$')
      if (regex.test(pathname)) {
        return pagesDictionary[pattern].title
      }
    }

    return 'Página não encontrada'
  }
}

/**
 * Resolver for event page titles based on URL parameters.
 */
class EventPageTitleResolver {
  constructor() {
    this.eventTitles = {
      new: 'Cadastrar evento',
      readonly: 'Visualizar evento',
      edit: 'Editar evento'
    }
  }

  /**
   * Checks if this resolver can handle the given pathname.
   * @param {string} pathname - The current pathname.
   * @returns {boolean} True if this resolver can handle the pathname.
   */
  canResolve(pathname) {
    return pathname === '/benefits/event' || (pathname.startsWith('/events/') && pathname !== '/events')
  }

  /**
   * Resolves the page title for event pages.
   * @param {string} pathname - The current pathname.
   * @param {string} asPath - The full path including query parameters.
   * @returns {string} The resolved page title.
   */
  resolve(pathname, asPath) {
    if (asPath.includes('readonly=true')) {
      return this.eventTitles.readonly
    }

    if (asPath.includes('event=new')) {
      return this.eventTitles.new
    }

    if (asPath.includes('event=') && !asPath.includes('event=new')) {
      return this.eventTitles.edit
    }

    if (pathname.startsWith('/events/') && pathname !== '/events') {
      return this.eventTitles.edit
    }

    return this.eventTitles.edit
  }
}

/**
 * Resolver for page titles using a predefined dictionary.
 */
class DictionaryPageTitleResolver {
  /**
   * Checks if this resolver can handle the given paths.
   * @param {string} pathname - The current pathname.
   * @param {string} asPath - The full path including query parameters.
   * @returns {boolean} True if this resolver can handle the paths.
   */
  canResolve(pathname, asPath) {
    const pathWithoutQuery = asPath.split('?')[0]
    return pagesDictionary[pathWithoutQuery] || pagesDictionary[pathname]
  }

  /**
   * Resolves the page title using the dictionary.
   * @param {string} pathname - The current pathname.
   * @param {string} asPath - The full path including query parameters.
   * @returns {string} The resolved page title.
   */
  resolve(pathname, asPath) {
    const pathWithoutQuery = asPath.split('?')[0]

    if (pagesDictionary[pathWithoutQuery]) {
      return pagesDictionary[pathWithoutQuery].title
    }

    return pagesDictionary[pathname]?.title || 'Página não encontrada'
  }
}

/**
 * Main resolver that coordinates multiple strategies using Chain of Responsibility pattern.
 */
class PageTitleResolver {
  constructor() {
    this.resolvers = [new EventPageTitleResolver(), new DictionaryPageTitleResolver(), new DynamicRouteResolver()]
  }

  /**
   * Resolves the page title by trying each resolver in order.
   * @param {string} pathname - The current pathname.
   * @param {string} asPath - The full path including query parameters.
   * @returns {string} The resolved page title.
   */
  resolve(pathname, asPath) {
    for (const resolver of this.resolvers) {
      if (resolver.canResolve(pathname, asPath)) {
        return resolver.resolve(pathname, asPath)
      }
    }

    return 'Página não encontrada'
  }

  /**
   * Adds a new resolver to the beginning of the resolvers list.
   * @param {Object} resolver - The resolver to add, must have canResolve and resolve methods.
   */
  addResolver(resolver) {
    this.resolvers.unshift(resolver)
  }
}

export const pageTitleResolver = new PageTitleResolver()

/**
 * Utility function for backward compatibility.
 * @param {string} pathname - The current pathname.
 * @param {string} asPath - The full path including query parameters.
 * @returns {string} The resolved page title.
 */
export const getPageTitle = (pathname, asPath) => {
  return pageTitleResolver.resolve(pathname, asPath)
}
